import { db } from 'boot/firebase';
import { collection } from 'firebase/firestore';
import { head, isEmpty, map, omit, shuffle } from 'lodash';
import DataDB from 'src/fb/data-db';
import { logEvent } from 'src/utils/LogEvent';

import GenericDB from './generic-db';

export default class SessionProgramsDB extends GenericDB {
  constructor(sessionId) {
    super('', 'sessions.sessionPrograms');
    this.sessionId = sessionId;
  }

  setSessionId(sessionId) {
    this.sessionId = sessionId;
  } // setSessionId()

  getCollectionRef() {
    return collection(db, 'sessions', this.sessionId, 'sessionPrograms');
  }

  /**
   * Reads SessionPrograms for the session with in specified order (or shuffled)
   * @param {*} orderBy - shuffleOrder to randomize SessionPrograms
   * @param {*} orderByDirection
   */
  async readOrdered(orderBy = 'order', orderByDirection = 'asc') {
    let sessionProgramsRaw;
    if (orderBy === 'shuffleOrder') {
      sessionProgramsRaw = await this.readAll();
      sessionProgramsRaw = shuffle(sessionProgramsRaw);
      let order = 0;
      sessionProgramsRaw = sessionProgramsRaw
        .map(((sessionProgramRaw) => {
          order += 1;
          return { ...sessionProgramRaw, order };
        }));
    } else {
      sessionProgramsRaw = await this.readAll(null, orderBy, orderByDirection);
    }
    return sessionProgramsRaw;
  } // readOrdered()

  /**
   * Reads SessionPrograms for the session and merges associated programs into the result
   * @param {*} orderBy
   * @param {*} orderByDirection
   * @param {string|null} clientId
   */
  async readWithData(
    orderBy = 'order',
    orderByDirection = 'asc',
    clientId = null,
  ) {
    const dataDb = new DataDB();
    const sessionProgramsRaw = await this.readAll(null, orderBy, orderByDirection);
    if (isEmpty(sessionProgramsRaw)) {
      return [];
    }
    // Map details about each program to the session program
    // Again: The program can be mapped to multiple session-programs because a single program
    // can be used multiple times throughout a session
    const sessionProgramActions = map(
      sessionProgramsRaw,
      async (sessionProgram) => {
        const data = await dataDb.readAll(
          [
            ['client', '==', clientId],
            ['sessionProgram', '==', sessionProgram.id],
          ],
          null,
          null,
          1,
        );
        return {
          ...sessionProgram,
          data: head(data),
        };
      },
    );

    return Promise.all(sessionProgramActions);
  } // readWithData()

  /**
   * Promise to create a batch of documents in the collection. Cannot update existing data.
   * @param {string} sessionId
   * @param {Array<Object>} sessionPrograms
   */
  async addProgramsToSession(sessionId, sessionPrograms) {
    const currentSessionPrograms = await this.readAll();
    const existingSessionPrograms = [];

    const deleteSessionProgramsActions = currentSessionPrograms
      .map(async (currentSessionProgram) => {
        const isInNewPrograms = sessionPrograms.find((newProgram) => {
          return newProgram?.id === currentSessionProgram?.id;
        });

        if (!isInNewPrograms) {
          return this.delete(currentSessionProgram.id);
        }

        existingSessionPrograms.push(currentSessionProgram);
      });

    await Promise.all(deleteSessionProgramsActions);

    const preparedSessionPrograms = this.#prepareSessionPrograms(sessionId, sessionPrograms);
    const sessionProgramActions = preparedSessionPrograms
      .map((sessionProgram) => {
        const currentExistingProgram = existingSessionPrograms.find((existingProgram) => {
          return existingProgram?.program === sessionProgram.program;
        });

        if (currentExistingProgram && currentExistingProgram.id) {
          return this.update({ ...sessionProgram, id: currentExistingProgram.id });
        }

        return this.create(sessionProgram);
      });
    return Promise.all(sessionProgramActions);
  } // addProgramsToSession()

  /**
   * Prepare session programs for saving by applying session id and cleaning up order prop
   * @param {string} sessionId
   * @param {Array<Object>} sessionPrograms
   */
  // eslint-disable-next-line class-methods-use-this
  #prepareSessionPrograms(sessionId, sessionPrograms) {
    return sessionPrograms.reduce((acc, sessionProgram) => {
      if (!sessionProgram.program?.id) {
        const errorMessage = 'Session program is missing program ID';
        console.error(errorMessage, sessionProgram);
        logEvent(errorMessage, {
          context: {
            context_data: {
              sessionProgram,
            },
          },
        });
        return acc;
      }

      const currentOrder = acc.length + 1;
      acc.push({
        ...omit(sessionProgram, 'id'),
        program: sessionProgram.program.id,
        session: sessionId,
        order: currentOrder,
      });

      return acc;
    }, []);
  }
}
